import { requiredDurableStore } from "../lib/clientStorage";
import { supabase } from "../lib/supabase";
import { withSmsSendIntent, readSmsIntent } from "./smsSendIntent";
import { api } from "../lib/api";
import { smsShortlistLinks } from "../lib/smsShortlistLinks";

// TRAIN-2 · client messaging thread (PKT-0452 shape).
// Reads/writes /api/clients/{id}/sms* — the backend's gated surface. The send
// path behind this is routes/sms.py's consent gate; this service never talks
// to Twilio and never bypasses anything.

export type ConsentState = "granted" | "denied" | "unknown";

export type DeliveryStatus =
  | "queued"
  | "sending"
  | "sent"
  | "delivered"
  | "undelivered"
  | "failed"
  | "scaffold"
  | "outcome_unknown";

export interface ClientThreadMessage {
  send_action_id?: string | null;
  history_source?: string | null;
  id: string;
  direction: "in" | "out";
  // 'media_bounce' and 'help_reply' are automated system copy; the thread
  // renders them differently from human messages.
  kind: "message" | "media_bounce" | "help_reply";
  body: string;
  num_media: number;
  // Twilio delivery state on outbound rows (via the /sms/status callback);
  // null on inbound rows and pre-callback sends.
  status?: DeliveryStatus | null;
  // When the TEAM first viewed this inbound row; null = unread. SMS has no
  // client-side read receipts, so this is operator read state only.
  read_at?: string | null;
  created_at?: string | null;
}

export interface ClientThreadResponse {
  client_id: string;
  client_phone: string | null;
  consent_state: ConsentState;
  // Ch78 · HOW they consented (the ledger row's source): website_form ·
  // inbound_first_message · operator_attested · carrier_start · carrier_stop.
  // Absent/null when consent_state is unknown or the backend predates it.
  consent_source?: string | null;
  messages: ClientThreadMessage[];
  lead_history_unavailable?: boolean;
}

export interface SendRefusal {
  reason: string;
  message: string;
  guards_passed: string[];
}

// TRAIN-MOBILE · one row per client with thread messages — the Messages
// page's inbox. Backend truncates the preview to 100 chars and computes
// unread_count (inbound rows with null read_at) + consent_state server-side.
export interface SmsConversation {
  client_id: string;
  client_name: string;
  client_phone: string | null;
  last_message_body: string;
  last_message_at: string | null;
  last_direction: "in" | "out" | string;
  last_kind: string;
  unread_count: number;
  consent_state: ConsentState;
}

export async function fetchSmsConversations(agent: string = "mine"): Promise<SmsConversation[]> {
  // Ch78 · same scope rule as the queue: default = the signed-in user's clients.
  const q = agent && agent !== "mine" ? `?agent=${encodeURIComponent(agent)}` : "";
  return api.get<SmsConversation[]>(`/clients/sms-conversations${q}`);
}

export async function fetchClientSmsThread(
  clientId: string
): Promise<ClientThreadResponse> {
  const thread = await api.get<ClientThreadResponse>(`/clients/${clientId}/sms-thread`);
  // Recovery is read-only. A provider callback may have settled a lost response.
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user.id) {
    const key = `sms_send:${session.user.id}:${clientId}`;
    const pending = readSmsIntent(key);
    if (pending && thread.messages.some(m => m.send_action_id === pending.id && m.status !== 'outcome_unknown')) {
      requiredDurableStore.removeItem(key);
    }
  }
  return thread;
}

export interface SmsTemplateSelection {
  template_id: string;
  template_version: string;
  rendered_body: string;
}

export async function sendClientSms(clientId: string, rawBody: string, templateSelections?: SmsTemplateSelection[]): Promise<ClientThreadMessage> {
  // F27 · the send chokepoint, not only the insert buttons: a list link pasted
  // by hand (e.g. from Copy link) must also go out without "https://".
  const body = smsShortlistLinks(rawBody);
  return withSmsSendIntent(clientId, [body, templateSelections ?? []], id => api.post<ClientThreadMessage>(`/clients/${clientId}/sms`, {
    body, send_action_id: id, ...(templateSelections?.length ? {template_selections: templateSelections} : {}),
  }), result => result.status === 'outcome_unknown');
}

export async function markClientSmsThreadRead(
  clientId: string
): Promise<{ marked_read: number }> {
  return api.post<{ marked_read: number }>(
    `/clients/${clientId}/sms-thread/read`,
    {}
  );
}

export async function attestClientSmsConsent(
  clientId: string,
  note: string
): Promise<{ consent_state: ConsentState; phone: string }> {
  return api.post(`/clients/${clientId}/sms-consent`, { note });
}
